import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NodeAppBar from "./NodeAppBar";
import StepNode from "../Nodes/StepNode";
import { getRevAllNodesWithStatus } from "../../../../redux/procedureRevisionSlice";
import Loader from "../../../../common/Loader";
import { setTaskReference } from "../../../Tasks/Uitlities";
import { isLocked } from "../../Utils/utils";
import { lockTask, pingTask } from "../../../../util/apiService";
import { showSnackbar } from "../../../../redux/snackbarSlice";
import useApiTabLock from "../../../../hooks/useApiTabLock";
import { useRevisionLock } from "../../../../contexts/RevisionLockContext";

/**
 * ViewStep Component
 *
 * Displays step details when user checks the checkbox before step name
 * Route: /view-step/:partnumber/:revision/:partId/:revisionId/:section_id_ref/:step_id_ref
 */
const ViewStep = () => {
  const {
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
  } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { allRevisionNodes, currentStatus } = useSelector(
    (state) => state.procedureRevision
  );
  const authUser = useSelector((state) => state.auth.user);

  const [loading, setLoading] = useState(true);
  const [pingInterval, setPingInterval] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const is_deleted = searchParams.get("is_deleted");

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  const routeKey = `revision_${revisionId}`;

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const { isLocked: isApiLocked, isActiveTab, lockedBy } = useApiTabLock(`revision_${revisionId}`);

  // Tab order lock from provider
  const revisionLock = useRevisionLock();
  const isFirstTabByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isFirstTab : false;
  const isLockedByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isLocked : true;

  // State to track if we should temporarily bypass tab order lock
  // Initialize by checking if THIS specific tab has a bypass flag
  const [bypassTabOrderLock, setBypassTabOrderLock] = useState(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const bypassFlag = sessionStorage.getItem(`tabOrderLockBypass_${revisionId}_${tabId}`);
    return bypassFlag === 'true';
  });

  // State to track if tab lock is initialized
  const [tabLockInitialized, setTabLockInitialized] = useState(false);

  // Track when tab lock state is properly initialized
  useEffect(() => {
    // Consider tab lock initialized when we have a definitive state
    const timer = setTimeout(() => {
      setTabLockInitialized(true);
    }, 100); // Small delay to ensure state is settled

    return () => clearTimeout(timer);
  }, [isFirstTabByOrder, bypassTabOrderLock]);

  // Leadership restoration - Check if this tab was the leader before navigation
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const fromDocument = sessionStorage.getItem(`fromDocument_${revisionId}`) === 'true';
    const wasLeader = sessionStorage.getItem(`wasLeader_${revisionId}_${tabId}`) === 'true';
    
    if (fromDocument && wasLeader) {
      setBypassTabOrderLock(true);
      sessionStorage.setItem(`tabOrderLockBypass_${revisionId}_${tabId}`, 'true');
    }
  }, [revisionId]);

  // Track when this tab becomes the leader
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    if (isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revisionId}_${tabId}`, 'true');
    }
  }, [isFirstTabByOrder, revisionId]);

  const isLock = isLocked(currentStatus);

  // Derive Data from Redux Store
  const sectionData = useMemo(() => {
    return allRevisionNodes?.find((section) => section.tree_node_id === section_id_ref);
  }, [allRevisionNodes, section_id_ref]);

  const stepData = useMemo(() => {
    return sectionData?.steps?.find((step) => step.tree_node_id === step_id_ref);
  }, [sectionData, step_id_ref]);

  const stepVersionHistoryMatchIds = useMemo(
    () =>
      [
        step_id_ref,
        stepData?.step_id_ref,
        stepData?.detail?.step_tree_node_id,
      ].filter(Boolean),
    [
      step_id_ref,
      stepData?.step_id_ref,
      stepData?.detail?.step_tree_node_id,
    ],
  );

  /** Aligns with post-load globalStepNumber used in the step heading (procedure-wide step index). */
  const globalStepOrdinalForNodeHistory = useMemo(() => {
    if (!section_id_ref || !step_id_ref || !Array.isArray(allRevisionNodes)) {
      return null;
    }
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...allRevisionNodes].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const sectionIndex = sortedSections.findIndex(
      (section) => section.tree_node_id === section_id_ref,
    );
    if (sectionIndex < 0) return null;

    const sec = sortedSections[sectionIndex];
    const steps = sec?.steps || [];
    const stepIdx = steps.findIndex((st) => st.tree_node_id === step_id_ref);
    if (stepIdx < 0) return null;

    const globalStepStart = sortedSections
      .slice(0, sectionIndex)
      .reduce((total, section) => total + (section.steps?.length || 0), 0);

    return globalStepStart + stepIdx + 1;
  }, [allRevisionNodes, section_id_ref, step_id_ref]);

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);
        await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
            is_deleted: is_deleted,
          })
        );
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref && step_id_ref) {
      fetchNodes();
    }
  }, [partId, revisionId, section_id_ref, step_id_ref, dispatch, is_deleted]);

  // Set task reference for this step - only when data changes
  // Must be called before any early returns (React Hooks rule)
  useEffect(() => {
    if (stepData && partnumber) {
      setTaskReference(
        dispatch,
        // `Step ${globalStepNumber}, ${partnumber} Rev ${revision}`,
        `Step ${partnumber}`,
        stepData?.detail?.step_tree_node_id,
        stepData?.tree_node_id,
        stepData?.ltree_path
      );
    }
  }, [stepData, partnumber, dispatch]);

  const onNavigate = async (nodeType, nodeData, context) => {
    // Call lock API when checkbox is clicked
    try {
      if (authUser && stepData) {
        const lockData = {
          task_id: revisionId, // Use revision ID as task_id
          user_id: authUser.id,
          user_name: `${authUser.first_name} ${authUser.last_name}`,
          action: "edit"
        };

        await lockTask(lockData);
        dispatch(showSnackbar({
          message: "Step locked successfully",
          severity: "success"
        }));
        // Start ping interval after successful lock
        startPingInterval();
      }
    } catch (error) {
      console.error('Failed to lock step:', error);
      dispatch(showSnackbar({
        message: "Failed to lock step",
        severity: "error"
      }));
    }
  };

  // Start ping interval function
  const startPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }

    const interval = setInterval(async () => {
      try {
        if (authUser) {
          const pingData = {
            task_id: revisionId,
            user_id: authUser.id
          };
          await pingTask(pingData);
        }
      } catch (error) {
        console.error('Ping failed:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    setPingInterval(interval);
  };

  // Stop ping interval function
  const stopPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      setPingInterval(null);
    }
  };

  // Cleanup ping interval on component unmount
  useEffect(() => {
    return () => {
      stopPingInterval();
    };
  }, [pingInterval]);

  // Handle Add dropdown navigation
  const handleAddElement = (type) => {
    if (type === "Step") {
      navigate(
        `/add-new-step/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}?step_id_ref=${step_id_ref}`
      );
    } else if (type === "Substep") {
      navigate(
        `/add-new-substep/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
      );
    } else if (type === "Attachment") {
      navigate(
        `/add-new-attachment/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
      );
    } else if (type === "Canvas") {
      // Clear all canvas-related storage before adding
      const clearAllCanvasData = async () => {
        try {
          // Clear localStorage items
          localStorage.removeItem("canvasPages");
          localStorage.removeItem("canvas_pages");
          localStorage.removeItem("canvas_title");
          localStorage.removeItem("canvas_description");
          localStorage.removeItem("canvasSnapshot");
          localStorage.removeItem("canvasState");
          localStorage.removeItem("short_description");
          localStorage.removeItem("editCanvasReturningFromPreview");
          // Clear sessionStorage items
          sessionStorage.removeItem("canvasStateInfo");
          // Clear IndexedDB - canvasElements store
          try {
            const { openCanvasDB } = require("../../../../util/indexedDBUtils");
            const db = await openCanvasDB();
            if (db && db.objectStoreNames.contains("canvasElements")) {
              const transaction = db.transaction(
                ["canvasElements"],
                "readwrite"
              );
              const store = transaction.objectStore("canvasElements");
              await new Promise((resolve, reject) => {
                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
              });
              db.close();
            }
          } catch (error) {
            console.warn("⚠️ Error clearing canvasElementsDB:", error);
          }

          // Clear IndexedDB - CanvasStorageDB
          try {
            const canvasStorageDB = await new Promise((resolve, reject) => {
              const req = indexedDB.open("CanvasStorageDB");
              req.onsuccess = (e) => resolve(e.target.result);
              req.onerror = (e) => reject(e.target.error);
            });

            if (
              canvasStorageDB &&
              canvasStorageDB.objectStoreNames.contains("canvasStorage")
            ) {
              const transaction = canvasStorageDB.transaction(
                ["canvasStorage"],
                "readwrite"
              );
              const store = transaction.objectStore("canvasStorage");
              await new Promise((resolve, reject) => {
                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
              });
              canvasStorageDB.close();
            }
          } catch (error) {
            console.warn("⚠️ Error clearing CanvasStorageDB:", error);
          }

        } catch (error) {
          console.error("❌ Failed to clear canvas data for Add mode:", error);
        }
      };

      // Wait for clearing to complete before navigating
      clearAllCanvasData().then(() => {
        navigate(
          `/add-new-canvas/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`,
          {
            state: {
              returnTo: `${location.pathname}${location.search || ""}`,
            },
          }
        );
      });
    } else if (type === "Form") {
      navigate(
        `/add-new-form/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
      );
    } else if (type === "Rich Text") {
      navigate(
        `/add-new-richtext/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
      );
    } else if (type === "Video") {
      navigate(
        `/add-new-video/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
      );
    }
  };

  // Handle Edit button click
  const handleEditStep = () => {
    navigate(
      `/edit-step/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`
    );
  };

  if (loading) {
    return <Loader text="Loading step details..." />;
  }

  if (!stepData) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Step not found.</p>
      </div>
    );
  }

  // Calculate indices from data
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...(allRevisionNodes || [])].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  const sectionIndex = sortedSections.findIndex(
    (section) => section.tree_node_id === section_id_ref
  );

  const stepIndex =
    sectionData?.steps?.findIndex(
      (step) => step.tree_node_id === step_id_ref
    ) || 0;

  // Calculate global step number
  const globalStepStart = sortedSections
    .slice(0, sectionIndex)
    .reduce((total, section) => total + (section.steps?.length || 0), 0);
  const globalStepNumber = globalStepStart + stepIndex + 1;

  const stepHeading = `Step ${globalStepNumber}`;

  return (
    <>
      <NodeAppBar
        title={stepHeading}
        isStep={true}
        isSelectedPage={true}
        onAddElement={handleAddElement}
        onEditStep={handleEditStep}
        partNumber={partnumber}
        revision={revision}
        partId={partId}
        revisionId={revisionId}
        sectionId={section_id_ref}
        sectionIndex={sectionIndex}
        stepId={step_id_ref}
        stepIndex={stepIndex}
        globalStepNumber={globalStepNumber}
        ltree_path={stepData?.ltree_path}
        is_deleted={stepData?.is_deleted || false}
        isLocked={isLock || (!tabLockInitialized ? false : (!bypassTabOrderLock && !isFirstTabByOrder)) || (isApiLocked && lockedBy)}
        versionHistoryFallbackVersionId={
          stepData?.detail?.version_id_ref || stepData?.version_id_ref
        }
        versionHistoryNodeId={step_id_ref}
        versionHistoryNodeIdField="step_tree_node_id"
        versionHistoryMatchIds={stepVersionHistoryMatchIds}
        versionHistoryStepOrdinal={globalStepOrdinalForNodeHistory}
      />

      <div style={{ padding: "20px" }}>
        {/* Render the step with its nodes */}
        <StepNode
          step={stepData}
          stepIndex={stepIndex}
          globalStepNumber={globalStepNumber}
          sectionIndex={sectionIndex}
          sectionHeading={`Section ${sectionIndex + 1}`}
          sectionId={section_id_ref}
          stepId={step_id_ref}
          onNavigate={onNavigate}
          checkboxState={{
            currentNodeId: step_id_ref,
            currentNodeType: "step",
            allChildrenChecked: true,
            allChildrenDisabled: true,
          }}
          disabled={isLock || (!tabLockInitialized ? false : (!bypassTabOrderLock && !isFirstTabByOrder)) || (isApiLocked && lockedBy)}
          allNodesData={allRevisionNodes}
        />
      </div>
    </>
  );
};

export default ViewStep;
